import { PipelineSeriesByRegionColour, PipelineSeriesOnBuild } from '@nova/types';

import { BasePipeline } from '../glPipelines/pipeline-base';
import { TexturePipelineBase } from '../glPipelines/pipeline-texBase';
import { ColourMatrixTexturePipeline } from '../glPipelines/pipeline-texture-colourMatrix';
import { MaskedTexturePipeline } from '../glPipelines/pipeline-texture-masking';
import { OverlayedTexturePipeline } from '../glPipelines/pipeline-texture-overlay';
import { ScaledTexturePipeline } from '../glPipelines/pipeline-texture-scale';
import { SummationTexturePipeline } from '../glPipelines/pipeline-texture-summation';
import { TiledTexturePipeline } from '../glPipelines/pipeline-texture-tiling';
import { TintedTexturePipeline } from '../glPipelines/pipeline-texture-tinting';
import { IScaleParams } from '../models/scale-params.interface';
import { IScalePipelineResult } from '../models/scale-pipeline-result.interface';
import { TextureManipulationDrawSeries } from '../texture-manip-drawSeries';
import { TextureManipulationSeriesRegionMasking } from '../texture-manip-drawSeriesByRegion';

import { IRenderPipeline } from './render-pipeline.interface';

// Runs a one-shot WebGL pipeline and always disposes of it afterward (buffers, shaders, canvas).
// Used for single-operation helpers (copy, mask, tint, etc.) so GPU resources are not retained.
function runDisposablePipeline<T>(pipeline: BasePipeline, operation: () => T): T {
  try {
    return operation();
  }
  finally {
    pipeline.Destroy();
  }
}

export class WebGLRenderPipeline implements IRenderPipeline {
  public readonly type = 'webgl' as const;

  private readonly gl: WebGLRenderingContext;
  private readonly ownsContext: boolean;

  public constructor(gl: WebGLRenderingContext, ownsContext = false) {
    this.gl = gl;
    this.ownsContext = ownsContext;
  }

  public getGlContext(): WebGLRenderingContext {
    return this.gl;
  }

  public init(_width: number, _height: number): void {}

  public resize(_width: number, _height: number): void {}

  public destroy(): void {
    if (this.ownsContext) {
      this.gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  }

  public copy(image: ImageData): Uint8Array {
    const pipeline = new TexturePipelineBase();
    pipeline.Init(image.width, image.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([image]);
      return pipeline.DrawOnceForPixelData();
    });
  }

  public tile(image: ImageData, finalWidth: number, finalHeight: number): Uint8Array {
    const pipeline = new TiledTexturePipeline(finalWidth / image.width, finalHeight / image.height);
    pipeline.Init(finalWidth, finalHeight, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([image]);
      return pipeline.DrawOnceForPixelData();
    });
  }

  public mask(imageToMask: ImageData, imageAsMask: ImageData, invertMaskEffect: boolean): Uint8Array {
    const pipeline = new MaskedTexturePipeline(invertMaskEffect);
    pipeline.Init(imageToMask.width, imageToMask.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([imageToMask, imageAsMask]);
      return pipeline.DrawOnceForPixelData();
    });
  }

  public overlay(imageBottom: ImageData, imageTop: ImageData): Uint8Array {
    const pipeline = new OverlayedTexturePipeline();
    pipeline.Init(imageBottom.width, imageBottom.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([imageBottom, imageTop]);
      return pipeline.DrawOnceForPixelData();
    });
  }

  public tint(image: ImageData, colour: number[]): Uint8Array {
    const pipeline = new TintedTexturePipeline();
    pipeline.Init(image.width, image.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([image]);
      return pipeline.DrawOnceForPixelData(colour);
    });
  }

  public scale(image: ImageData, scaleParams: IScaleParams): IScalePipelineResult {
    const pipeline = new ScaledTexturePipeline(scaleParams);
    pipeline.Init(image.width, image.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      const newWidthPx = pipeline.canvasWidth;
      const newHeightPx = pipeline.canvasHeight;
      pipeline.CreateTextures([image]);
      const pixelData = pipeline.DrawOnceForPixelData();
      return { pixelData, newWidthPx, newHeightPx };
    });
  }

  public colourMatrix(image: ImageData, matrix: Float32Array | number[], alpha: number): Uint8Array {
    const colourMatrixPipeline = new ColourMatrixTexturePipeline();
    colourMatrixPipeline.Init(image.width, image.height, this.gl);
    return runDisposablePipeline(colourMatrixPipeline, () => {
      colourMatrixPipeline.CreateTextures([image]);
      return colourMatrixPipeline.DrawOnceForPixelData({
        matrix_4x5: matrix instanceof Float32Array ? matrix : new Float32Array(matrix),
        alpha
      });
    });
  }

  public summation(imageA: ImageData, imageB: ImageData): Uint8Array {
    const pipeline = new SummationTexturePipeline();
    pipeline.Init(imageA.width, imageA.height, this.gl);
    return runDisposablePipeline(pipeline, () => {
      pipeline.CreateTextures([imageA, imageB]);
      return pipeline.DrawOnceForPixelData();
    });
  }

  public runPipelineSeries(base: ImageData, seriesBuilt: PipelineSeriesOnBuild): Uint8Array {
    if (seriesBuilt.length === 0) {
      return this.copy(base);
    }

    const texManipSeries = new TextureManipulationDrawSeries();
    try {
      texManipSeries.InitPipelines(this.gl, base.width, base.height);
      texManipSeries.CreateBaseTexture(base);
      const lastType = texManipSeries.Draw(seriesBuilt);
      return texManipSeries.GetPixelData(lastType);
    }
    finally {
      texManipSeries.Destroy();
    }
  }

  public runPipelineSeriesByRegionColour(
    base: ImageData,
    mask: ImageData,
    seriesByRegionColour: PipelineSeriesByRegionColour
  ): Uint8Array {
    if (Object.keys(seriesByRegionColour).length === 0) {
      return this.copy(base);
    }

    const texManipSeriesMasked = new TextureManipulationSeriesRegionMasking();
    try {
      texManipSeriesMasked.InitPipelines(this.gl, base.width, base.height);
      texManipSeriesMasked.CreateBlankOutputTexture(base.width, base.height);
      texManipSeriesMasked.CreateBaseTexture(base);
      texManipSeriesMasked.CreateMaskTexture(mask);
      return texManipSeriesMasked.DrawAndGetPixelData(seriesByRegionColour);
    }
    finally {
      texManipSeriesMasked.Destroy();
    }
  }
}
